import { FC, ReactNode } from 'react'

import { styled } from 'styled-components'

import Icon from 'core/components/Icon'
import Clickable from 'core/components/lib/Clickable'
import { onSurfaceVariant } from 'core/styles/tokens'

export type PaginationProps = {
  className?: string
  hasPrevious: boolean
  hasNext: boolean
  onPrevious: () => void
  onNext: () => void
  /** Disables both buttons while a page is being fetched. */
  isLoading?: boolean
  /** Optional counter slot, e.g. "1–25 of 300". */
  label?: ReactNode
}

/**
 * Render-only prev/next pager. Pair with useCursorPager (NewPaginator
 * responses) or useOffsetPager (legacy offset/limit endpoints), which
 * produce these props.
 */
const Pagination: FC<PaginationProps> = ({
  className,
  hasPrevious,
  hasNext,
  onPrevious,
  onNext,
  isLoading = false,
  label,
}) =>
  !hasPrevious && !hasNext ? null : (
    <Container className={className}>
      {label != null && <Label>{label}</Label>}
      <Clickable
        ariaLabel='Previous page'
        disabled={!hasPrevious || isLoading}
        onClick={onPrevious}
        variant='ghost'
      >
        <Icon fontSize='20px' name='keyboard_arrow_left' />
      </Clickable>
      <Clickable
        ariaLabel='Next page'
        disabled={!hasNext || isLoading}
        onClick={onNext}
        variant='ghost'
      >
        <Icon fontSize='20px' name='keyboard_arrow_right' />
      </Clickable>
    </Container>
  )

export default Pagination

const Container = styled.div`
  display: flex;
  gap: 4px;
  align-items: center;
  justify-content: flex-end;
`

const Label = styled.span`
  margin-right: 8px;
  color: ${(p) => p.theme.labelText ?? onSurfaceVariant};
  font-size: 12px;
`
